/**
 * Which English marketing paths have a Tamil counterpart under /ta.
 *
 * ── Why this is not derived from the content files ────────────────────────
 * MarketingShell is a client component, and importing moduleLanding.ts or
 * cityLandingTa.ts into it would ship every word of long-form marketing copy
 * in both languages to the browser to answer a yes/no question. The Tamil
 * tree mirrors whole branches, so prefix rules answer it exactly and for
 * nothing. scripts/route-drift.mjs checks these rules against what is
 * actually on disk, so they cannot quietly go stale.
 *
 * ── Why it matters that this is exact ─────────────────────────────────────
 * The language toggle renders `rel="alternate" hrefLang="ta-IN"`. Pointing
 * that at a page that does not exist is worse than having no toggle: the
 * reader gets a 404 in the language they just asked for, and a crawler is
 * told a Tamil version exists when it does not. So the toggle is rendered
 * only where the counterpart is real.
 */

/** Exact English paths with a Tamil twin. */
const TA_EXACT = new Set(['/', '/modules', '/pricing', '/contact']);

/** English branches mirrored wholesale under /ta. */
const TA_PREFIXES = ['/modules/', '/construction-erp-software/'];

/** True when `pathname` (an English path) has a /ta counterpart. */
export function hasTamilVersion(pathname: string): boolean {
  if (TA_EXACT.has(pathname)) return true;
  return TA_PREFIXES.some((p) => pathname.startsWith(p));
}

/** The Tamil path for an English one. Only meaningful if hasTamilVersion(). */
export const toTamilPath = (pathname: string) => (pathname === '/' ? '/ta' : `/ta${pathname}`);

/** The English path for a Tamil one. Every /ta page mirrors an English path. */
export const toEnglishPath = (pathname: string) => pathname.replace(/^\/ta/, '') || '/';
